import api from '../api/client';
import LoggingService from '@shared/js/LoggingService';

const STATE = {
	INITIAL:     'initial',
	INITIALIZED: 'initialized',
};

const MESSAGE_NAMESPACE = 'urn:x-cast:tv.asport';
const MESSAGE_CHANNEL_MAX_ATTEMPTS = 3;
const MESSAGE_CHANNEL_RETRY_DELAY_MS = 250;

const EVENT = {
	// base
	INIT:                        'init',
	CASTING_START:               'cast-start',
	CASTING_STOP:                'cast-stop',
	PROGRESS:                    'progress',
	// remote events
	ANY_CHANGE:                  'anyChanged',
	IS_CONNECTED_CHANGED:        'isConnectedChanged',
	IS_MEDIA_LOADED_CHANGED:     'isMediaLoadedChanged',
	DURATION_CHANGED:            'durationChanged',
	CURRENT_TIME_CHANGED:        'currentTimeChanged',
	IS_PAUSED_CHANGED:           'isPausedChanged',
	VOLUME_LEVEL_CHANGED:        'volumeLevelChanged',
	CAN_CONTROL_VOLUME_CHANGED:  'canControlVolumeChanged',
	IS_MUTED_CHANGED:            'isMutedChanged',
	CAN_PAUSE_CHANGED:           'canPauseChanged',
	CAN_SEEK_CHANGED:            'canSeekChanged',
	DISPLAY_NAME_CHANGED:        'displayNameChanged',
	STATUS_TEXT_CHANGED:         'statusTextChanged',
	TITLE_CHANGED:               'titleChanged',
	DISPLAY_STATUS_CHANGED:      'displayStatusChanged',
	MEDIA_INFO_CHANGED:          'mediaInfoChanged',
	IMAGE_URL_CHANGED:           'imageUrlChanged',
	PLAYER_STATE_CHANGED:        'playerStateChanged',
	LIVE_SEEKABLE_RANGE_CHANGED: 'liveSeekableRange',
};

// Reference:
// - https://developers.google.com/cast/docs/web_sender/integrate
// - https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayer#volumeLevel
// - https://developers.google.com/cast/docs/reference/web_sender/cast.framework.RemotePlayerController#seek

// const RECEIVER_APP = chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID;
const RECEIVER_APP = '4EA1EA86'; // prod
// const RECEIVER_APP = '18566B80'; // dev

class Chromecast {
	_state = STATE.INITIAL;
	_player = null;
	_playerController = null;
	_eventListeners = Object.values(EVENT).reduce((acc, event) => {
		// @ts-ignore
		acc[event] = [];
		return acc;
	}, {});

	_sessionMessageSubscriptionMap = new Map();
	_eventId = null;
	_eventPassword = null;

	constructor() {
		const isBlueTv = window.$Helpers && window.$Helpers.isBlueTv();
		if (isBlueTv) return;

		window.__onGCastApiAvailable = (isAvailable) => {
			if (isAvailable) {
				this.init();
			} else {
				console.log('Chromecast is not available on this client.');
			}
		};
		const script = document.createElement('script');
		script.src = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';
		document.body.append(script);
	}

	addEventListener(event, handler) {
		this._eventListeners[event].push(handler);

		if (event === EVENT.INIT && this._state !== STATE.INITIAL) {
			handler();
		}
	}

	setEventId(value) {
		this._eventId = value;
	}

	setEventPassword(value) {
		this._eventPassword = value;
	}

	removeEventListener(event, handler) {
		this._eventListeners[event] = this._eventListeners[event].filter((currentHandler) => currentHandler !== handler);
	}

	emit(event, payload) {
		for (const handler of this._eventListeners[event]) {
			handler(payload);
		}
	}

	get isAvailable() {
		return this._state === STATE.INITIALIZED;
	}

	// eslint-disable-next-line class-methods-use-this
	get instance() {
		return window.cast?.framework?.CastContext?.getInstance() ?? null;
	}

	get currentSession() {
		return this.instance?.getCurrentSession() ?? null;
	}

	async createSession() {
		const instance = this.instance;
		if (!instance) {
			throw new Error('Chromecast is not initialized.');
		}

		await instance.requestSession();
		if (!this.currentSession) {
			throw new Error('Casting session was not created.');
		}
		return this.currentSession;
	}

	async connect() {
		const session = this.currentSession ?? (await this.createSession());
		this.addSessionMessageListener(session);
		return session;
	}

	// Register the custom channel as soon as a session exists so it is ready before LOAD starts.
	addSessionMessageListener(session) {
		if (!this._sessionMessageSubscriptionMap.has(session)) {
			session.addMessageListener(MESSAGE_NAMESPACE, (namespace, message) => {
				this.handleSessionMessage(session, namespace, message);
			});
			this._sessionMessageSubscriptionMap.set(session, true);
		}
	}

	// Resolve receiver playout requests and report the exact failing handshake stage.
	async handleSessionMessage(session, namespace, message) {
		let stage = 'parse-message';

		try {
			const messageData = JSON.parse(message);
			console.log('[chromecast] @message urn:x-cast:tv.asport', { namespace, message, eventId: this._eventId });
			if (messageData.type !== 'PLAYOUT_REQUEST') return;

			const userAgent = messageData.data.ua;
			console.log('[chromecast] @message playout request', { userAgent });
			stage = 'request-playout';
			const stream = await api.getEventStream(this._eventId, this._eventPassword, userAgent);
			console.log('[chromecast] @message playout response', { stream });

			stage = 'send-playout-response';
			const url = stream?.hlsUrl || null;
			await session.sendMessage(MESSAGE_NAMESPACE, {
				type: 'PLAYOUT_RESOLVE',
				data: { url },
			});
		} catch (error) {
			this.reportError(error, stage, session);
			this.emit(EVENT.CASTING_STOP);
		}
	}

	// Keep Cast diagnostics useful without sending stream URLs or event passwords to Sentry.
	reportError(error, stage, session = null) {
		const errorMessage = error instanceof Error
			? error.message
			: (typeof error === 'string' ? error : (error?.description || error?.code || 'Unknown Chromecast error'));
		const reportedError = new Error(`Chromecast ${stage} failed: ${errorMessage}`);

		if (error instanceof Error && error.stack) {
			reportedError.stack = error.stack;
		}

		LoggingService.logError(reportedError, {
			scope:   'chromecast',
			context: {
				stage,
				eventId:      this._eventId,
				sessionState: session?.getSessionState?.() ?? null,
				errorCode:    (typeof error === 'string' ? error : error?.code) ?? null,
			},
		});
	}

	// Sending a no-op message establishes the custom channel before LOAD triggers the receiver handshake.
	async prepareMessageChannel(session) {
		let lastError = null;

		for (let attempt = 1; attempt <= MESSAGE_CHANNEL_MAX_ATTEMPTS; attempt++) {
			try {
				await session.sendMessage(MESSAGE_NAMESPACE, {
					type: 'SENDER_READY',
				});
				return;
			} catch (error) {
				lastError = error;
				if (attempt < MESSAGE_CHANNEL_MAX_ATTEMPTS) {
					await new Promise((resolve) => setTimeout(resolve, MESSAGE_CHANNEL_RETRY_DELAY_MS));
				}
			}
		}

		throw lastError;
	}

	get player() {
		if (!this._player) throw new Error('Player is not initialized.');
		return this._player;
	}

	get playerController() {
		if (!this._playerController) throw new Error('Player is not initialized.');
		return this._playerController;
	}

	get state() {
		if (this._state === STATE.INITIAL) return STATE.INITIAL;
		return this.player.playerState;
	}

	get isConnected() {
		return this.player.isConnected;
	}

	get isPaused() {
		return this.player.isPaused;
	}

	get isMediaLoaded() {
		return this.player.isMediaLoaded;
	}

	get position() {
		return this.player.currentTime;
	}

	get duration() {
		return this.player.duration;
	}

	play() {
		if (!this.player.isPaused) return;
		this.playerController.playOrPause();
	}

	pause() {
		if (this.player.isPaused) return;
		this.playerController.playOrPause();
	}

	playOrPause() {
		this.playerController.playOrPause();
	}

	seek(position) {
		this.player.currentTime = position;
		this.playerController.seek();
	}

	setVolume(volume) {
		this.player.volumeLevel = volume / 100;
		this.playerController.setVolumeLevel();
	}

	get isMuted() {
		return this.player.isMuted;
	}

	mute() {
		if (this.player.isMuted) return;
		this.playerController.muteOrUnmute();
	}

	unmute() {
		if (!this.player.isMuted) return;
		this.playerController.muteOrUnmute();
	}

	async init() {
		const castFramework = window.cast?.framework;
		const chromeCast = window.chrome?.cast;

		if (!castFramework || !chromeCast?.AutoJoinPolicy) return;

		const instance = this.instance;
		if (!instance) return;

		instance.setOptions({
			receiverApplicationId: RECEIVER_APP,
			autoJoinPolicy:        chromeCast.AutoJoinPolicy.ORIGIN_SCOPED,
		});

		instance.addEventListener(castFramework.CastContextEventType.SESSION_STATE_CHANGED, (event) => {
			console.log('SESSION_STATE_CHANGED', event);
			if ([castFramework.SessionState.SESSION_STARTED, castFramework.SessionState.SESSION_RESUMED].includes(event.sessionState)) {
				try {
					this.addSessionMessageListener(event.session ?? this.currentSession);
				} catch (error) {
					this.reportError(error, 'register-message-listener', event.session);
				}
				this.emit(EVENT.CASTING_START);
			}
			if (event.sessionState === castFramework.SessionState.SESSION_ENDED) {
				this.emit(EVENT.CASTING_STOP);
			}

			if (event.sessionState === castFramework.SessionState.SESSION_START_FAILED && event.errorCode !== chromeCast.ErrorCode.CANCEL) {
				this.reportError(new Error(`Chromecast session start failed: ${event.errorCode}`), 'start-session', event.session);
			}

			if (event.sessionState === castFramework.SessionState.SESSION_START_FAILED) {
				this.emit(EVENT.CASTING_STOP);
			}
		});

		this._player = new castFramework.RemotePlayer();
		this._playerController = new castFramework.RemotePlayerController(this._player);

		// any change
		this._playerController.addEventListener(castFramework.RemotePlayerEventType.ANY_CHANGE, () => {
			this.emit(EVENT.ANY_CHANGE);
		});

		// media info change (currentTime, metadata)
		this._playerController.addEventListener(castFramework.RemotePlayerEventType.MEDIA_INFO_CHANGED, () => {
			if (this.currentSession) {
				const mediaSession = this.currentSession.getMediaSession();
				if (mediaSession) {
					const position = mediaSession.getEstimatedTime();
					this.emit(EVENT.PROGRESS, position);
				}
			}

			this.emit(EVENT.MEDIA_INFO_CHANGED);
		});

		this._playerController.addEventListener(castFramework.RemotePlayerEventType.IS_CONNECTED_CHANGED, () => {
			console.log('IS_CONNECTED_CHANGED', this.isConnected);
			if (this.player.isConnected) {
				this.emit(EVENT.CASTING_START);
			} else {
				this.emit(EVENT.CASTING_STOP);
			}
			this.emit(EVENT.IS_CONNECTED_CHANGED);
		});

		this._playerController.addEventListener(castFramework.RemotePlayerEventType.IS_MEDIA_LOADED_CHANGED, () => {
			this.emit(EVENT.IS_MEDIA_LOADED_CHANGED);
		});

		this._state = STATE.INITIALIZED;
		this.emit(EVENT.INIT);
	}

	/**
	 * Cast media to the Chromecast device
	 * @param {string} url - The media URL to cast
	 * @param {Object} options - Cast options
	 * @param {number|null} options.audioTrackId - Audio track index to select
	 * @param {number} options.position - Playback position (currentTime)
	 * @param {string} options.contentType - MIME type
	 * @param {boolean} options.isLive - Whether this is a live stream
	 * @param {Object|null} options.liveSeekableRange - For live streams with DVR, pass the video element's seekable range:
	 *   { start: video.seekable.start(0), end: video.seekable.end(0) }
	 *   This enables the Chromecast to start playback at the same position relative to live edge,
	 *   and provides the full DVR window context.
	 */
	async castMedia(url, { audioTrackId = null, position = 0, contentType = 'application/x-mpegurl', isLive = false, liveSeekableRange = null }) {
		let stage = 'connect-session';
		let session = null;

		try {
			session = await this.connect();

			stage = 'prepare-message-channel';
			await this.prepareMessageChannel(session);

			stage = 'create-load-request';
			const mediaInfo = new chrome.cast.media.MediaInfo(url, contentType);
			mediaInfo.streamType = isLive ? chrome.cast.media.StreamType.LIVE : chrome.cast.media.StreamType.BUFFERED;

			const request = new chrome.cast.media.LoadRequest(mediaInfo);
			request.autoplay = true;
			request.currentTime = position;

			// For live streams, include additional positioning data
			// so the receiver can seek to the correct position within the DVR window
			if (isLive && liveSeekableRange) {
				const offsetFromLiveEdge = liveSeekableRange.end - position;

				request.customData = {
					audioTrackId,
					isLive:            true,
					// The playback position at the time of casting
					livePosition:      position,
					// How many seconds behind the live edge the user was watching
					offsetFromLiveEdge,
					// The full seekable range from the browser player
					liveSeekableRange: {
						start: liveSeekableRange.start,
						end:   liveSeekableRange.end,
					},
				};
			} else {
				request.customData = {
					audioTrackId,
				};
			}

			stage = 'load-media';
			await session.loadMedia(request);
		} catch (error) {
			if (error !== window.chrome?.cast?.ErrorCode?.CANCEL && error?.code !== window.chrome?.cast?.ErrorCode?.CANCEL) {
				this.reportError(error, stage, session);
			}
			this.emit(EVENT.CASTING_STOP);
		}
	}

	stopCasting() {
		this.playerController.stop();
		if (!this.currentSession) return;
		this.currentSession.endSession(true);
	}
}

export { Chromecast, EVENT };
export default Chromecast;
